import { AuthService } from "@/services/auth-service";
import Axios from "axios";
import {Axios as apiAxios} from "@/utils/axios";
import { v4 } from "uuid";
import { BrowserInfo } from "../browser";
import { getNewElectronDefaultPrinterSettings, IElectronDefaultPrinterSettings, IElectronPrinter } from "./electron-printer";
import { ServerConfig } from "@/config/config";
import { getDefaultElectronNfcSettings, IElectronNfcSettings, ElectronNfcReader } from "./electron-nfc";


export interface IOpenElectronWindowOptions{
	show:boolean;
	offscreen:boolean;
}

export interface IElectronFileEntry{
	name:string;
	path:string;
	isDirectory:boolean;
}

let nativeWindowOpen:any = window.open;
function windowOpenOverride(...args:any):any{
	console.log("Overriding window open");
	if (typeof(args[0]) == "string" && args[1] == "_blank"){
		console.log(args[0]);
		if (args[0].charAt(0) == "/") {
			let url = new URL(`${window.location.origin}${args[0]}`);
			Axios.post(`${BrowserInfo.getElectronUrl()}/app/open-new-tab`, {url: url.toString()});
			return null;
		}
	}
	return nativeWindowOpen(...args);
}


window.close = function(){
	ElectronFunctions.getIpcRenderer().invoke("close-this-tab");
};

// setLocalStorage override function
let setLocalStorageTimeout = 0;
let nativeLocalStorageSetItem = localStorage.setItem.bind(localStorage);
let lastLocalStorageSet = new Date();
function setLocalStorage(key:string, value:string){
	nativeLocalStorageSetItem(key, value);
	let now = new Date();
	now.setSeconds(now.getSeconds() - 1);
	if (lastLocalStorageSet.getTime() > now.getTime()) {
		clearTimeout(setLocalStorageTimeout);
		setLocalStorageTimeout = setTimeout(()=>{
			ElectronFunctions.saveLocalStorage().then(()=>{
				lastLocalStorageSet = new Date();
			});
		}, 1000);
	}else{
		ElectronFunctions.saveLocalStorage().then(()=>{
			lastLocalStorageSet = new Date();
		});
	}
}





class ElectronFunctionsClass {
	getDefaultPrinterIdForSaleLayout(saleLayoutId:number):string {
		return localStorage.getItem(`electron-sale-layout-default-printer-${AuthService.wfDossier.id}-${saleLayoutId}`) || "";
	}

	setDefaultPrinterIdForSaleLayout(saleLayoutId:number, value:string){
		let key = `electron-sale-layout-default-printer-${AuthService.wfDossier.id}-${saleLayoutId}`;
		if (!value) {
			localStorage.removeItem(key);
			this.saveLocalStorageInDatabase(key, "");
		}else{
			localStorage.setItem(key, value);
			this.saveLocalStorageInDatabase(key, value);
		}
	}

	getDefaultPrinterIdForLabelLayout(labelLayoutId:number):IElectronDefaultPrinterSettings {
		let value = localStorage.getItem(`electron-label-layout-default-printer-${AuthService.wfDossier.id}-${labelLayoutId}`) || "";
		if (!value) {
			return getNewElectronDefaultPrinterSettings();
		}
		try{
			return JSON.parse(value);
		}catch(err){
			let settings = getNewElectronDefaultPrinterSettings();
			settings.deviceId = value;
			return settings;
		}
	}

	setDefaultPrinterIdForLabelLayout(labelLayoutId:number, value?:IElectronDefaultPrinterSettings){
		let key = `electron-label-layout-default-printer-${AuthService.wfDossier.id}-${labelLayoutId}`;
		if (!value || !value.deviceId) {
			localStorage.removeItem(key);
			this.saveLocalStorageInDatabase(key, "");
		}else{
			let toSetValue = JSON.stringify(value);
			localStorage.setItem(key, toSetValue);
			this.saveLocalStorageInDatabase(key, toSetValue);
		}
	}

	getCopyCountForSaleLayout(saleLayoutId:number):number{
		let res = parseInt(localStorage.getItem(`electron-sale-layout-default-copy-count-${AuthService.wfDossier.id}-${saleLayoutId}`) || "0");
		if (Number.isNaN(res) || res == 0){
			return 1;
		}
		return res;
	}

	setCopyCountForSaleLayout(saleLayoutId:number, copy:number){
		let key = `electron-sale-layout-default-copy-count-${AuthService.wfDossier.id}-${saleLayoutId}`;
		if (copy < 2){
			localStorage.removeItem(key);
			this.saveLocalStorageInDatabase(key, "");
		}else{
			let value = `${copy}`;
			localStorage.setItem(key, value);
			this.saveLocalStorageInDatabase(key, value);
		}
	}

	getDefaultPrinterIdForReminderLayout(reminderLayoutId:number):string {
		return localStorage.getItem(`electron-reminder-layout-default-printer-${AuthService.wfDossier.id}-${reminderLayoutId}`) || "";
	}

	setDefaultPrinterIdForReminderLayout(reminderLayoutId:number, value:string){
		let key = `electron-reminder-layout-default-printer-${AuthService.wfDossier.id}-${reminderLayoutId}`;
		if (!value) {
			localStorage.removeItem(key);
			this.saveLocalStorageInDatabase(key, "");
		}else{
			localStorage.setItem(key, value);
			this.saveLocalStorageInDatabase(key, value);
		}
	}

	getNfcSettings():IElectronNfcSettings{
		let key = `electron-nfc-settings-${AuthService.wfCompany.ID}`;
		let resultStr = localStorage.getItem(key);
		if (!resultStr) {
			return getDefaultElectronNfcSettings();
		}
		return JSON.parse(resultStr);
	}

	setNfcSettings(value:IElectronNfcSettings) {
		let key = `electron-nfc-settings-${AuthService.wfCompany.ID}`;
		localStorage.setItem(key, JSON.stringify(value));
	}

	setDownloadFolderForSaleType(saleTypeId:number, path:string | null){
		let key = `electron-dossier-${AuthService.wfDossier.id}-sale-type-${saleTypeId}-download-path`;
		if (path) {
			if (path.endsWith("/")){
				path = path.substring(0, path.length-2);
			}
			localStorage.setItem(key, path);
			this.saveLocalStorageInDatabase(key, path);
		}else{
			localStorage.removeItem(key);
			this.saveLocalStorageInDatabase(key, "");
		}
	}

	getDownloadFolderForSaleType(saleTypeId:number):string | null {
		let key = `electron-dossier-${AuthService.wfDossier.id}-sale-type-${saleTypeId}-download-path`;
		return localStorage.getItem(key);
	}

	getCopyCountForReminderLayout(reminderLayoutId:number):number{
		let res = parseInt(localStorage.getItem(`electron-reminder-layout-default-copy-count-${AuthService.wfDossier.id}-${reminderLayoutId}`) || "0");
		if (Number.isNaN(res) || res == 0){
			return 1;
		}
		return res;
	}

	setCopyCountForReminderLayout(reminderLayoutId:number, copy:number){
		let key = `electron-reminder-layout-default-copy-count-${AuthService.wfDossier.id}-${reminderLayoutId}`;
		if (copy < 2){
			localStorage.removeItem(key);
			this.saveLocalStorageInDatabase(key, "");
		}else{
			let value = `${copy}`;
			localStorage.setItem(key, value);
			this.saveLocalStorageInDatabase(key, value);
		}
	}

	async saveTempFile(file:File):Promise<string> {
		let form = new FormData();
		form.set("file", file, file.name);
		await Axios.post(`${BrowserInfo.getElectronUrl()}/fs/temp-file`, form, {headers: {
			"Content-Type": "form/multipart"
		}});
		return `${BrowserInfo.getElectronUrl()}/fs/temp-file/${file.name}`;
	}

	async deleteTempFile(fileName:string):Promise<void> {
		await Axios.delete(`${BrowserInfo.getElectronUrl()}/fs/temp-file/${fileName}`);
	}

	async printTempFile(fileName:string):Promise<void> {
		await Axios.post(`${BrowserInfo.getElectronUrl()}/fs/print-temp-file`, {fileName});
	}

	async getTempFilePath(fileName:string):Promise<string> {
		return (await Axios.get(`${BrowserInfo.getElectronUrl()}/fs/temp-file-path/${fileName}`)).data.path;
	}

	getPdfViewerUrl(fileName:string, autoprint:boolean = false):string {
		return `${BrowserInfo.getElectronUrl()}/extensions/pdf-viewer/web/viewer.html?file=${BrowserInfo.getElectronUrl()}/fs/temp-file/${fileName}?autoprint=${autoprint?"1":"0"}`;
	}

	async getPrinters():Promise<IElectronPrinter[]>{
		let result = await Axios.get(`${BrowserInfo.getElectronUrl()}/printers`);
		return result.data as IElectronPrinter[];
	}

	async printPdf(pdfFile:Blob, printerId:string, copies:number = 0, printInLandscape:boolean = false):Promise<void>{
		let fileName = `${v4()}.pdf`;
		let file = new File([pdfFile], fileName, {type: "application/pdf"});
		let deleteUrl = await this.saveTempFile(file);
		let err:any = null;
		try{
			await Axios.post(`${BrowserInfo.getElectronUrl()}/printers/print-temp-file`, {fileName, deviceId: printerId, copies, landscape: printInLandscape});
		}catch(error){
			err = error;
		}
		Axios.delete(deleteUrl);
		if (err) {
			throw err;
		}
	}

	async printTempFileWithDialog(data:Blob, fileName:string):Promise<void>{
		fileName = `${v4()}.pdf`;
		let fileTemp = new File([data], fileName, {type: data.type});
		let file = fileTemp as File;
		let deleteUrl = await this.saveTempFile(file);
		await Axios.post(`${BrowserInfo.getElectronUrl()}/printers/print-temp-file-with-dialog`, {fileName});
		Axios.delete(deleteUrl);
	}

	async testServer(url:string){
		await this.getSerialNumber(url);
	}

	async getSerialNumber(url=BrowserInfo.getElectronUrl()):Promise<{key:string,name:string}>{
		let result = await Axios.get(`${url}/extensions/serial-number`);
		return result.data as any;
	}

	async downloadFileToPath(file:Blob, path:string){
		let body = new FormData();
		body.set("file", file);
		await Axios.post(`${BrowserInfo.getElectronUrl()}/fs/save-file`, body, {params: {filePath: path}});
	}

	async lsDirectory(path:string):Promise<IElectronFileEntry[]>{
		let result = await Axios.get(`${BrowserInfo.getElectronUrl()}/fs/ls`, {params: {path}});
		return result.data;
	}

	async getHomeDir():Promise<string>{
		let result = await Axios.get(`${BrowserInfo.getElectronUrl()}/fs/home-dir`);
		return result.data || "/";
	}

	public getIpcRenderer():any {
		let nativeRequire = new Function("package", "return require(package)");
		let {ipcRenderer} = nativeRequire("electron");
		return ipcRenderer;
	}

	private initializeElectronSessionStorage(){
		let ipcRenderer = this.getIpcRenderer();
		sessionStorage.setItem = (key:string, value:string)=>{
			ipcRenderer.send("set-session-storage", key, value);
		};

		sessionStorage.getItem = (key:string)=> {
			return ipcRenderer.sendSync("get-session-storage", key);
		};

		sessionStorage.clear = ()=>{
			ipcRenderer.send("clear-session-storage");
		};
	}

	initializeElectron(){
		window.open = windowOpenOverride;
		localStorage.setItem = setLocalStorage;
		this.initializeElectronSessionStorage();
		ElectronNfcReader.init();
		window.addEventListener("keydown", (event)=>{
			if (event.key == "F12" || (event.key.toLowerCase() == "i" && ((event.ctrlKey && event.shiftKey) || (event.metaKey && event.altKey)))){
				window.parent.postMessage("toggle-dev-tools");
			}
			if (event.ctrlKey){
				if (event.key == "=" || event.key == "+") {
					console.log("Zoom in");
					window.parent.postMessage("zoom-in");
				}else if (event.key == "-"){
					console.log("Zoom out");
					window.parent.postMessage("zoom-out");
				}
			}
		});

		window.addEventListener("wheel", (event)=>{
			if (!event.ctrlKey) return;
			if (event.deltaY > 0){
				window.parent.postMessage("zoom-out");
			}else{
				window.parent.postMessage("zoom-in");
			}
		});
	}

	openElectronWindow(url:string, options:IOpenElectronWindowOptions){
		console.log("Open electron window");
		new Function("url", "options", `
			console.log("Inside Function");
			let {ipcRenderer} = require("electron");
			console.log("Got Renderer");
			ipcRenderer.send("window-open", {
				url,
				options
			});
		`)(url, options);
	}

	async saveLocalStorage(){
		let result:any = {};
		for (let i = 0; i < localStorage.length; i++) {
			let key = localStorage.key(i) || "";
			result[key] = localStorage.getItem(key);
		}
		await Axios.post(`${BrowserInfo.getElectronUrl()}/app/save-local-storage`, result);
	}

	async saveLocalStorageInDatabase(key:string, value:string){
		if (!BrowserInfo.hasElectronConfigured()) return;
		try{
			let serialNumber = await this.getSerialNumber();
			await apiAxios.post(`${ServerConfig.host}/device-config/${encodeURIComponent(serialNumber.key)}/${encodeURIComponent(key)}`, value);
		}catch(err){
			console.log(`Failed to save local storage backup key: ${key} value: ${value}`);
		}
	}
}

export const ElectronFunctions = new ElectronFunctionsClass();
